import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const UnifiedSelectorModal = ({ isOpen, onClose, onSelect, type, existingItems = [] }) => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState('all');

  const config = {
    events: {
      title: 'Select Wedding Events',
      subtitle: 'Choose events to add to your timeline',
      icon: '🎉',
      emptyMessage: 'No events available',
      fetchData: async () => {
        const response = await apiService.weddingEvents.getAll();
        return response.events || [];
      }
    },
    savedRituals: {
      title: 'Select Your Saved Rituals',
      subtitle: 'Choose from rituals you\'ve saved',
      icon: '🎭',
      emptyMessage: 'You haven\'t saved any rituals yet',
      emptyLink: '/wedcraft-studio/cultural-ritual-explainer',
      emptyLinkText: 'Browse Cultural Rituals →',
      fetchData: async () => {
        const response = await apiService.userRituals.getAll();
        return response.rituals || [];
      }
    },
    allRituals: {
      title: 'Select Cultural Rituals',
      subtitle: 'Choose from all available rituals',
      icon: '🎭',
      emptyMessage: 'No rituals available',
      fetchData: async () => {
        const response = await apiService.rituals.getAll();
        return response.rituals || [];
      }
    }
  };

  const currentConfig = config[type] || config.events;

  useEffect(() => {
    if (isOpen) {
      fetchItems();
      // Don't reset selections - they'll be set by the next useEffect
      setFilterValue('all'); // Reset filter when modal opens
    }
  }, [isOpen, type]);

  useEffect(() => {
    // When items are loaded, pre-select those already in staged events
    if (items.length > 0 && existingItems.length > 0) {
      const preSelected = items.filter(item => 
        existingItems.some(existing => {
          const itemRitualId = item.ritual_id || item.id;
          return existing.ritualId === itemRitualId || existing.id === itemRitualId;
        })
      );
      setSelectedItems(preSelected);
    } else if (items.length > 0 && existingItems.length === 0) {
      // Clear selections if no existing items
      setSelectedItems([]);
    }
  }, [items, existingItems]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await currentConfig.fetchData();
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleAdd = () => {
    if (selectedItems.length === 0) {
      alert('Please select at least one item');
      return;
    }
    onSelect(selectedItems);
    // Don't clear selections - keep them for next time modal opens
    onClose();
  };

  const handleClose = () => {
    // Don't clear selections - they should persist
    onClose();
  };

  // Get unique filter options based on type
  const getFilterOptions = () => {
    if (type === 'events') {
      const categories = [...new Set(items.map(item => item.category).filter(Boolean))];
      return categories.map(cat => ({ value: cat, label: cat.charAt(0).toUpperCase() + cat.slice(1) }));
    } else if (type === 'savedRituals' || type === 'allRituals') {
      const cultures = [...new Set(items.map(item => item.culture_display_name).filter(Boolean))];
      return cultures.sort().map(culture => ({ value: culture, label: culture }));
    }
    return [];
  };

  // Filter items based on selected filter
  const filteredItems = filterValue === 'all' 
    ? items 
    : items.filter(item => {
        if (type === 'events') {
          return item.category === filterValue;
        } else if (type === 'savedRituals' || type === 'allRituals') {
          return item.culture_display_name === filterValue;
        }
        return true;
      });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-lg z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-gradient-to-br from-white/95 via-purple-50/30 to-pink-50/30 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-white/20 max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 p-8 rounded-t-3xl flex-shrink-0">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-4 left-4 text-4xl animate-pulse">{currentConfig.icon}</div>
            <div className="absolute top-8 right-8 text-3xl animate-bounce">✨</div>
            <div className="absolute bottom-4 left-1/3 text-2xl animate-pulse">💫</div>
          </div>
          
          <div className="relative">
            <h2 className="text-3xl font-serif font-bold text-white mb-2">{currentConfig.title}</h2>
            <p className="text-white/90">{currentConfig.subtitle}</p>
          </div>
          
          <button onClick={handleClose} className="absolute top-6 right-6 text-white/80 hover:text-white text-2xl">✕</button>
        </div>

        {/* Scrollable Content */}
        <div className="p-8 overflow-y-auto flex-1">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4 opacity-50">{currentConfig.icon}</div>
              {type === 'savedRituals' && !localStorage.getItem('token') ? (
                <p className="text-gray-600">Please log in to view your saved rituals</p>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">{currentConfig.emptyMessage}</p>
                  {currentConfig.emptyLink && (
                    <a 
                      href={currentConfig.emptyLink} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-700 font-medium underline"
                    >
                      {currentConfig.emptyLinkText}
                    </a>
                  )}
                </>
              )}
            </div>
          ) : (
            <>
              {/* Filter and Selection Info */}
              <div className="mb-6 flex items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-bold text-purple-600">{selectedItems.length}</span> item{selectedItems.length !== 1 ? 's' : ''}
                </p>
                
                {/* Filter Dropdown */}
                {getFilterOptions().length > 0 && (
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Filter:</label>
                    <select
                      value={filterValue}
                      onChange={(e) => setFilterValue(e.target.value)}
                      className="px-3 py-2 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                    >
                      <option value="all">All</option>
                      {getFilterOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredItems.map((item) => {
                  const isSelected = selectedItems.find(i => i.id === item.id);
                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleItem(item)}
                      className={`group relative cursor-pointer rounded-2xl p-4 border-2 transition-all duration-300 ${
                        isSelected
                          ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg scale-105'
                          : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
                      }`}
                    >
                      {/* Selection Indicator */}
                      <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <span className="text-white text-sm">✓</span>}
                      </div>

                      {/* Item Info */}
                      <div className="pr-8">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{item.icon}</span>
                          <h3 className="font-bold text-gray-800 group-hover:text-purple-600 transition-colors">
                            {item.name}
                          </h3>
                        </div>
                        
                        {item.description && (
                          <p className="text-xs text-gray-600 mb-2">{item.description}</p>
                        )}
                        
                        <div className="flex items-center gap-3 text-xs text-gray-600">
                          {item.culture_display_name && (
                            <span className="flex items-center">
                              <span className="mr-1">🌍</span>
                              {item.culture_display_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Sticky Footer Buttons */}
        {!loading && items.length > 0 && (
          <div className="flex gap-4 p-6 border-t border-gray-200 bg-white/80 backdrop-blur-sm rounded-b-3xl flex-shrink-0">
            <button
              onClick={handleAdd}
              disabled={selectedItems.length === 0}
              className="flex-1 bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 text-white py-3 px-6 rounded-xl font-bold hover:shadow-lg transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              ✨ Add {selectedItems.length > 0 ? `${selectedItems.length} ` : ''}to Timeline
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-bold hover:bg-gray-50 transition-all"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedSelectorModal;
